import {
    TenantConfigurationService,
} from "../services/tenant-configuration/tenant-configuration.service";
import * as angular from "angular";
import * as moment from "moment";
import {Match} from "../models/match.model";
import {Column, SortDirections} from "../models/column.model";
import {StudentMatchStatus, TestImportMatchStatus} from "../models/test-import-match-status.model";
import {ImportReportStatus, StudentImportPostOptions} from "../models/import-status.model";
import {ImportStatusService} from "../services/import-status/import-status.service";
import { TestImportService } from "../services/test-import/test-import.service";

const INIT_TEST_IMPORT_PROGRESS = "testImportProgress";
const INIT_IMPORT_STATUS = "importStatus";
const MATCHES_INIT_DATA = "matches";
const IS_NEW_EXPERIENCE = "isNewExperience";

interface ImportRecordsRows {
    schoolName: string;
    schoolId: string;
    status?: string;
    totalStudentsDeactivated?: string | number;
    totalStudentsCreated?: string | number;
    totalStudentsMatched?: string | number;
    totalStudentsTransferred?: string | number;
}

export class StudentImportRecordsController {
    progressWasChecked: boolean;
    isNewExperience: boolean;
    matches: Match[];
    columns: Column[];
    testImportProgress: TestImportMatchStatus;
    importReportStatus: ImportReportStatus;
    rows: ImportRecordsRows[];
    rowsCheckedState: {[key: string]: boolean};
    isPromptVisible: boolean = false;
    isImporting: boolean = false;
    isImportingComplete: boolean = false;
    showAlert: boolean = false;
    savedMatch: boolean = true;
    redoIntroVisible: boolean = false;
    checkboxChecked: boolean = false;
    pollInterval;
    isRedoStudentImportForbidden: boolean;
    hoursAfterAllowRedoImport: number;
    isFinalImportSuccessAlertVisible: boolean = false;
    idType: string = "sisId";
    previousIdType: string;

    /* @ngInject */
    constructor(
        private $routeParams: { districtId: string },
        private $route: angular.route.IRouteService,
        private $interval: angular.IIntervalService,
        private importStatusService: ImportStatusService,
        private $scope: angular.IScope,
        private tenantConfigurationService: TenantConfigurationService,
        private hasCheckboxes: boolean,
        private testImportService: TestImportService,
        private $location: angular.ILocationService,
        public feature: {[key:string]: boolean},
        private $rootScope: angular.IRootScopeService,
        private $timeout: angular.ITimeoutService,
        hoursAfterAllowRedoImport: {[key:string]: number}
    ) {
        this.isNewExperience = this.$route.current.locals[IS_NEW_EXPERIENCE];
        this.hoursAfterAllowRedoImport = hoursAfterAllowRedoImport.forStudent;
    }

    $onInit() {
        this.testImportProgress = this.$route.current.locals[INIT_TEST_IMPORT_PROGRESS];
        this.importReportStatus = this.$route.current.locals[INIT_IMPORT_STATUS];
        this.previousIdType = this.importReportStatus.matchCriteria;
        this.matches = this.$route.current.locals[MATCHES_INIT_DATA];
        this.columns = this.initColumns();
        this.rows = this.initRows();
        this.rowsCheckedState = this.createRowsCheckedState();

        if (this.hasImportingBegun()) {
            this.isImporting = true;
            this.importedColumns = this.hasCheckboxes ? this.columns.splice(1) : this.columns;
            this.pollTillAllSchoolsAreImported();
        }

        this.$scope.$on("$destroy", () => {
            this.$interval.cancel(this.pollInterval);
        });

        this.tenantConfigurationService.isCleverSSOEnabled(this.$routeParams.districtId).then((answer) => this.isCleverSSOEnabled = answer);

        this.$rootScope.$on("test-import-drawer:close", this.closeTestImportDrawer);

        this.isStudentImportCompleted();
    }

    private initColumns() {
        const checkBoxes = {
            headerName: "", field: "schoolName", maxWidth: 100, suppressSorting: true,
            cellRenderer: this.createRowCheckBox, headerCellRenderer: this.createHeaderCheckBox
        };

        const cols = [
            { headerName: "School", field: "schoolName", initialSort: ("ASC" as SortDirections) },
            { headerName: "Status", field: "status", cellRenderer: this.createRowStatus },
            { headerName: "Students Made Inactive", field: "totalStudentsDeactivated" },
            { headerName: "Students Created", field: "totalStudentsCreated" },
            { headerName: "Students Matched", field: "totalStudentsMatched" },
            { headerName: "Students Transferred", field: "totalStudentsTransferred" }
        ];

        if (this.hasCheckboxes) {
            cols.unshift(checkBoxes);
        }

        return cols;
    }

    private initRows(): ImportRecordsRows[] {
        return this.testImportProgress.studentMatchStatuses
            .map((school: StudentMatchStatus) => {
                if (this.isNewExperience) {
                    school.status = "Imported";
                } else {
                    school.status = this.getSchoolStatus(school.schoolId, this.importReportStatus);
                }

                return school;
            });
    }

    private getSchoolStatus(schoolId: string, data: ImportReportStatus) {
        if (this.isSchoolInProgress(schoolId, data)) {
            return "Import in progress";
        } else if (this.isSchoolImported(schoolId, data)) {
            return "Imported";
        }
        return "Test import complete";
    }

    private hasImportingBegun() {
        return !!this.importReportStatus && !!this.importReportStatus.startedOn;
    }

    private isSchoolInProgress(schoolId: string, data: ImportReportStatus) {
        return this.hasSchoolId(schoolId, data, "schoolsImported") &&
            this.hasSchoolId(schoolId, data, "schoolsMigrated") &&
            (data.totalSchoolsImported / data.totalSchoolsToBeImported < 1);
    }

    private isSchoolImported(schoolId: string, data: ImportReportStatus) {
        return this.hasSchoolId(schoolId, data, "schoolsImported") &&
            this.hasSchoolId(schoolId, data, "schoolsMigrated") &&
            (data.totalSchoolsImported / data.totalSchoolsToBeImported === 1);
    }

    private hasSchoolId(schoolId: string, data: ImportReportStatus, arr): boolean {
        if (!data || data[arr] == null) { return false; }
        return data[arr].some(el => el.schoolId === schoolId );
    }

    /* istanbul ignore next */
    private createRowCheckBox = params => {
        params.$scope.vm = this;

        return `
            <input
                type="checkbox"
                class="ns-checkbox"
                ng-disabled="vm.isSchoolStatusImported(data.status)"
                ng-checked="vm.isSchoolCheckedAndNotImported(data)"
                ng-click="vm.toggleRowCheckedState(data.schoolName)"
            />`;
    }

    /* istanbul ignore next */
    private createHeaderCheckBox = params => {
        params.$scope.vm = this;

        return `
            <input
                type="checkbox"
                class="ns-checkbox"
                ng-disabled="vm.allSchoolsAreImported()"
                ng-click="vm.toggleAllCheckboxes()"
                ng-checked="vm.isEveryRowChecked()"
            />`;
    }

    /* istanbul ignore next */
    private createRowStatus = (params) => {
        params.$scope.vm = this;

        return params.data.status === "Imported" ?
            `<span class="status-icon status-icon__imported">
                <i class="ns-icon ns-icon--size-large ns-icon--building-filled"></i>
            </span> Imported` :
            params.data.status;
    }

    private createRowsCheckedState(): {[key: string]: boolean} {
        return this.rows.reduce((acc, next) => {
            acc[`${next.schoolName}`] = this.isStatusNotImported(next.status);
            return acc;
        }, {});
    }

    private isStatusNotImported(status: string):boolean {
        return status !== "Imported";
    }

    isSchoolChecked(schoolName: string):boolean {
        return this.hasCheckboxes ?
        this.rowsCheckedState[schoolName] : true;
    }

    toggleRowCheckedState(schoolName: string):void {
        this.rowsCheckedState[schoolName] = !this.rowsCheckedState[schoolName];
    }

    private rowsCheckedValues(): boolean[] {
        return Object.keys(this.rowsCheckedState)
            .map(row => this.rowsCheckedState[row]);
    }

    private isEveryRowChecked():boolean {
        return this.rowsCheckedValues().every(val => val === true);
    }

    hasAtLeast1CheckedRow(): boolean {
        return this.rowsCheckedValues().some(val => val === true);
    }

    toggleAllCheckboxes():void {
        const newRowState = this.isEveryRowChecked();

        Object.keys(this.rowsCheckedState)
            .forEach(row => { this.rowsCheckedState[row] = !newRowState; });
    }

    openPrompt() { this.isPromptVisible = true; }
    togglePrompt() { this.isPromptVisible = !this.isPromptVisible; }

    private getSelectedRowIds(): string[] {
        return this.rows.reduce((acc, row) => {
            if (this.rowsCheckedState[row.schoolName] === true) {
                acc.push(row.schoolId);
            }
            return acc;
        }, []);
    }

    importedRows;
    importedColumns;
    initImport() {
        this.isPromptVisible = false;
        this.isImporting = true;
        this.importedColumns = this.hasCheckboxes ? this.columns.splice(1) : this.columns;

        const options: StudentImportPostOptions = {
            districtId: this.$routeParams.districtId,
            commandType: "MIGRATION",
            schoolsToImport: this.getSelectedRowIds()
        };

        this.importedRows = this.setImportedRows(this.importReportStatus);

        this.importStatusService.post(options)
            .then(() => {
                this.pollTillAllSchoolsAreImported();
                return;
            });

            //TODO: needs UX for failed POST
    }

    private setImportedRows(data: ImportReportStatus) {
        if (!data.schoolsMigrated || !data.schoolsImported) { return; }

        const mergedSchoolIds = [...data.schoolsImported, ...data.schoolsMigrated]
            .map(school => school.schoolId)
            .filter((el, i, arr) => arr.indexOf(el) === i);

        return mergedSchoolIds.reduce((acc, next) => {
            this.rows.forEach(row => {
                if (row.schoolId === next) {
                    acc.push({
                        schoolName: row.schoolName,
                        status: this.getSchoolStatus(next, data),
                        schoolId: next,
                        totalStudentsMatched: this.isImportingComplete ? row.totalStudentsMatched : "",
                        totalStudentsDeactivated: this.isImportingComplete ? row.totalStudentsDeactivated : "",
                        totalStudentsCreated: this.isImportingComplete ? row.totalStudentsCreated : "",
                        totalStudentsTransferred: this.isImportingComplete ? row.totalStudentsTransferred : ""
                    });
                }
            });

            return acc;
        }, []);
    }

    private pollTillAllSchoolsAreImported() {
        this.importStatusService.getAll(this.$routeParams.districtId)
            .then((response: ImportReportStatus) => {
                this.importReportStatus = response;
                this.updateImportedRows(response);
                this.conditionallyEnableClever(response);

                this.pollInterval = this.$interval(() => {
                    this.importStatusService.getAll(this.$routeParams.districtId)
                        .then((res: ImportReportStatus) => {
                            if ((!this.isNewExperience && this.isImportingComplete) || (this.isNewExperience && this.isImporting)) {
                                this.$interval.cancel(this.pollInterval);
                                this.showAlert = true;
                                return;
                            }
                            this.importReportStatus = res;
                            this.updateImportedRows(res);
                            this.conditionallyEnableClever(res);
                        });
                }, 1000);
            });
    }

    isCleverSSOEnabled: boolean;

    private conditionallyEnableClever(reportStatus: ImportReportStatus) {
        if (this.isCleverSSOEnabled || (reportStatus.schoolsImported && !reportStatus.schoolsImported.length)) { return; }

        this.tenantConfigurationService.enableClever(this.$routeParams.districtId).then(() => this.isCleverSSOEnabled = true);
    }

    private updateImportedRows(res: ImportReportStatus) {
        this.isImportingComplete =  !!this.importReportStatus.completedOn && parseInt(this.getProgressBarWidth(), 10) >= 100;
        this.importedRows = this.setImportedRows(res);
    }

    private getImportReportStatus() { return this.importReportStatus; }

    getProgressBarWidth() {
        if (!this.getImportReportStatus()) { return "0%"; }
        const migratedLength = this.getImportReportStatus().schoolsMigrated.length;
        const totalImported = this.getImportReportStatus().totalSchoolsImported;
        const toBeImported = this.getImportReportStatus().totalSchoolsToBeImported;
        
        let width = 0;

        if (typeof migratedLength !== "number" || typeof toBeImported !== "number" || typeof totalImported !== "number") {
            throw `Invalid input: migratedLength: ${migratedLength}, toBeImported: ${toBeImported}, totalImported: ${totalImported}`;
        }

        if (migratedLength > 0) {
            width = (migratedLength / toBeImported) * 5;
        }
        width += (totalImported / toBeImported) * 95;
        return `${width}%`;
    }

    isAlertVisible(): boolean {
        return this.isImportingComplete && this.showAlert;
    }

    closeAlert() { this.showAlert = false; }

    updateRowsForNewImport() {
        const importStatus = !this.importReportStatus.schoolsImported.length ?
            this.importReportStatus : this.importReportStatus;

        const importedIds = importStatus.schoolsImported.map(imported => imported.schoolId);

        this.rows = this.initRows().map(row => {
            if (importedIds.indexOf(row.schoolId) !== -1) { row.status = "Imported"; }
            return row;
        });

        this.columns = this.initColumns();

        this.rowsCheckedState = this.createRowsCheckedState();

        this.isImporting = false;
        this.isImportingComplete = false;
    }

    private isSchoolStatusImported(status): boolean {
        return status === "Imported";
    }

    isSchoolCheckedAndNotImported(school): boolean {
        return this.isSchoolChecked(school.schoolName) && !this.isSchoolStatusImported(school.status);
    }

    allSchoolsAreImported(): boolean {
        const importedSchools = this.rows.filter(row => row.status === "Imported").length;

        return this.rows.length === importedSchools && !this.hasAtLeast1CheckedRow();
    }

    allSchoolsAreNotImported(): boolean {
        return !this.allSchoolsAreImported() && !this.hasAtLeast1CheckedRow();
    }

    allSchoolsAreImportedOrNoRowIsChecked(): boolean {
        return this.allSchoolsAreImported() || !this.hasAtLeast1CheckedRow();
    }

    atLeastASchoolIsImported(): boolean {
        return this.rows.some(row => row.status === "Imported");
    }

    cancelRedoIntro() {
        this.redoIntroVisible = false;
    }

    redoTestImport(): void {
        this.testImportService.setLastStartedOnTimeForRedo(this.testImportProgress.startedOn);
        if (this.previousIdType === "studentNumber") {
            this.testImportService.redoMatchStudents(this.$route.current.params.districtId, "studentNumber").then(() => {
                this.$location.path(`/${this.$route.current.params.districtId}/import-student-data`);
            });
        } else {
            this.testImportService.redoMatchStudents(this.$route.current.params.districtId, this.idType).then(() => {
                this.$location.path(`/${this.$route.current.params.districtId}/import-student-data`);
            });
        }
    }

    getImportCompleteDate(): string {
        if (!(this.importReportStatus && this.importReportStatus.completedOn)) { return; }

        const momentLocal = (moment as any).tz(this.importReportStatus.completedOn, "UTC").local();
        return `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
    }

    getTestImportCompleteDate(): string {
        if (!this.testImportProgress) { return; }

        const momentLocal = (moment as any).tz(this.testImportProgress.completedOn, "UTC").local();
        return `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
    }

    getTotalCompletedSchools(): number {
        return this.importReportStatus.schoolsImported.length;
    }

    checkProgress() {
        this.progressWasChecked = true;
        this.importStatusService.getAll(this.$routeParams.districtId)
            .then((response: ImportReportStatus) => {
            this.importReportStatus = response;
            this.updateImportedRows(response);
            this.conditionallyEnableClever(response);
            this.isFinalImportSuccessAlertVisible = this.isStudentImportCompleted();
        });
        this.$timeout(() => {
            this.progressWasChecked = false;
        }, 5000);
    }

    isImportInProgress() {
        return this.isImporting && !this.isImportingComplete;
    }

    beginRedoOrDisplayTooltip(): void {
        if (this.isRedoStudentImportAllowed()) {
            this.isRedoStudentImportForbidden = false;
            this.redoIntroVisible = true;
        } else {
            this.isRedoStudentImportForbidden = true;
            this.redoIntroVisible = false;
        }
    }

    isRedoStudentImportAllowed(): boolean {
        if (!this.importReportStatus || !this.importReportStatus.nextAllowedImportTime) { return true; }

        return (new Date()).toISOString() >= this.importReportStatus.nextAllowedImportTime;
    }

    openTestImportDrawer() {
        this.isRedoStudentImportForbidden = false;
        this.$rootScope.$emit("test-import-drawer:open");
    }

    closeTestImportDrawer = (_event) => {
        this.$rootScope.$on("test-import-drawer:close", this.closeTestImportDrawer);
    }

    isFinalImportCompletedForNewExperience(): boolean {
        return !this.redoIntroVisible && this.isStudentImportCompleted() && this.isNewExperience;
    }

    isStudentImportCompleted(): boolean {
        if (!this.importReportStatus) { return false; }

        return (this.importReportStatus.startedOn !== undefined && this.importReportStatus.startedOn !== null) &&
        (this.importReportStatus.completedOn !== undefined && this.importReportStatus.completedOn !== null) &&
        this.importReportStatus.schoolsMigrated && this.importReportStatus.schoolsMigrated.length > 0 &&
        this.importReportStatus.schoolsImported && this.importReportStatus.schoolsImported.length > 0;
    }

    isFinalImportSuccessMessageVisible(): boolean {
        return this.isNewExperience && !this.redoIntroVisible && this.isImportingComplete;
    }

    shouldResultsViewShow() {
        return !this.redoIntroVisible 
            && ((!this.isImportInProgress() && this.isNewExperience) 
            || !this.isNewExperience);
    }

    isRedoBlockVisible(): boolean {
        return this.feature.redoStudentImportSection && this.isNewExperience && this.isStudentImportCompleted();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/student-import-records/student-import-records.controller.ts